import { ParameterHandler, safeJsonParse } from '@visiba/std';
import { ControllerContext } from './_controller_context';
import { ConvertMessageData } from './_convert_message_data';
import { Reporter } from './_reporter';
import { TransferFormat, WebSocketTransport } from './_web_socket_transport';
import { AxonEvent } from './axon_event';
import { HandshakeProtocol } from './handshake_protocol';
import { ReconnectionPolicy } from './reconnection_policy';

export enum HttpConnectionState {
	Disconnected = 0,
	Connecting = 1,
	Connected = 2,
	Disconnecting = 3,
	Reconnecting = 4,
}

/**
 * Module responsible to distribute and maintain a connection.
 */
export class HttpConnection {
	private static readonly LOG_TRACE = 'HttpConnection';

	public readonly parameterHandler: ParameterHandler;
	public readonly controllerContext: ControllerContext;
	public readonly handshakeProtocol: HandshakeProtocol;
	private readonly url: string;
	private readonly webSocketTransport: WebSocketTransport;
	private readonly reporter: Reporter;
	private readonly handshakeInterval: number;

	private connectionState: HttpConnectionState;
	private reconnectionPolicy: ReconnectionPolicy | null = null;

	public get state(): HttpConnectionState {
		return this.connectionState;
	}

	/** @internal */
	public onGracefulShutdown: (() => void) | null = null;
	/** @internal */
	public onReconnect: (() => void) | null = null;
	/** @internal */
	public onReconnected: (() => void) | null = null;

	/** @internal */
	public static create(
		url: string,
		handshakeInterval: number,
		webSocketTransport: WebSocketTransport,
		reporter: Reporter,
	): HttpConnection {
		return new HttpConnection(url, handshakeInterval, webSocketTransport, reporter);
	}

	private constructor(url: string, handshakeInterval: number, webSocketTransport: WebSocketTransport, reporter: Reporter) {
		this.parameterHandler = new ParameterHandler();

		this.url = url;
		this.handshakeInterval = handshakeInterval;

		this.reporter = reporter;

		this.controllerContext = ControllerContext.create(this, reporter);

		this.handshakeProtocol = new HandshakeProtocol(this.controllerContext, this.reporter);
		this.handshakeProtocol.onPanic = () => this.handlePanic();

		this.webSocketTransport = webSocketTransport;
		this.webSocketTransport.onMessage = (data) => this.handleOnMessage(data);
		this.webSocketTransport.onClose = (error?: Error) => this.handleOnWebSocketTransportClose(error);

		this.connectionState = HttpConnectionState.Disconnected;
	}

	public async open(): Promise<void> {
		this.connectionState = HttpConnectionState.Connecting;
		this.reporter.log(`[${HttpConnection.LOG_TRACE}] Sets connectionState to Connecting`);

		try {
			await this.openInternal();

			this.connectionState = HttpConnectionState.Connected;
			this.controllerContext.resyncUncoupledControllers();
			if (this.reconnectionPolicy != null) {
				this.controllerContext.calmControllers();
				this.onReconnected?.();
				this.reconnectionPolicy = null;
			}
			this.handshakeProtocol.sign(this.handshakeInterval);
			this.reporter.log(`[${HttpConnection.LOG_TRACE}] Sets connectionState to Connected`);
		} catch (error: unknown) {
			if (error instanceof Error) {
				this.reporter.log(`Error in open, reason ${error.message}`);
			} else if (error instanceof CloseEvent) {
				this.reporter.log(`Error in open, code ${error.code}`);
			} else {
				this.reporter.log(`Error in open, we have no idea why, see ${safeJsonParse(error) ?? error}`);
				console.log(error);
			}

			return Promise.reject(error);
		}
	}

	public send(data: string | ArrayBufferLike | Blob | ArrayBufferView): void {
		this.webSocketTransport.send(data);
	}

	public close(): Promise<void> {
		this.connectionState = HttpConnectionState.Disconnecting;
		this.reporter.log(`[${HttpConnection.LOG_TRACE}] Sets connectionState to Disconnecting`);

		return this.closeInternal();
	}

	private closeInternal(): Promise<void> {
		this.reconnectionPolicy?.dispose();
		this.reconnectionPolicy = null;
		this.handshakeProtocol.terminate();
		this.connectionState = HttpConnectionState.Disconnected;
		this.reporter.log(`[${HttpConnection.LOG_TRACE}] Sets connectionState to Disconnected`);

		return this.webSocketTransport.close();
	}

	private async openInternal(): Promise<void> {
		await this.webSocketTransport.connect(this.url + this.parameterHandler.getString(), TransferFormat.Text);
	}

	private handleOnMessage(data: string | ArrayBuffer): void {
		try {
			if (data instanceof ArrayBuffer) {
				throw new Error(`ArrayBuffer not yet impl`);
			}

			const message = ConvertMessageData.read(data);
			if (message.topic === AxonEvent.Pong) {
				this.handshakeProtocol.handlePong();

				return;
			}

			if (message.controller === 'system') {
				if (message.topic === AxonEvent.Error) {
					this.reporter.log(`System message carried an error: ${message.data}`);
				}

				return;
			}

			this.controllerContext.handleControllerMessage(message);
		} catch (error: unknown) {
			console.log(error);
			if (error instanceof Error) {
				this.reporter.log(`error while parsing a message: ${error.message}`);

				return;
			}

			this.reporter.log(`error while parsing a message... unknown type thrown, see -> ${safeJsonParse(error) ?? error}`);
		}
	}

	private async invokeReconnectionPolicy(): Promise<void> {
		if (this.reconnectionPolicy != null) return;
		this.reporter.log(`[${HttpConnection.LOG_TRACE}] invoked reconnectionPolicy`);

		this.connectionState = HttpConnectionState.Reconnecting;
		this.reporter.log(`[${HttpConnection.LOG_TRACE}] Sets connectionState to Reconnecting`);
		this.onReconnect?.();
		this.controllerContext.panicControllers();

		await this.closeInternal();

		this.reconnectionPolicy = new ReconnectionPolicy(
			{
				reconnectInterval: 1000,
				reconnectAscent: 1.5,
				reconnectCap: 300000,
				reconnectMaxAttempts: Infinity,
			},
			this.reporter,
		);
		this.reconnectionPolicy.invoke(this.handleReconnect.bind(this), this.gracefulShutdown.bind(this));
	}

	private async handleReconnect(): Promise<void> {
		await this.open();

		return Promise.resolve();
	}

	private handleOnWebSocketTransportClose(error?: Error): void {
		if (error != null) {
			this.reporter.log(`closed with error ${error.message}`);
			this.invokeReconnectionPolicy();

			return;
		}

		this.connectionState = HttpConnectionState.Disconnected;
		this.reporter.log(`[${HttpConnection.LOG_TRACE}] Sets connectionState to Disconnected`);
		// No error == Normal closure. Yup yup 👍
		this.gracefulShutdown();
	}

	private handlePanic(): void {
		this.reporter.log(`[${HttpConnection.LOG_TRACE}] panicked`);

		this.invokeReconnectionPolicy();
	}

	private gracefulShutdown(): void {
		if (this.onGracefulShutdown != null) {
			this.onGracefulShutdown();
		}
	}
}
